import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Spark, VoteType } from "@sparklab/types";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { DEFAULT_THEME, getThemeStyles } from "../themeConfig";
import { LikeButton } from "../components/LikeButton";
import { encodeSparkId } from "../utils/sparkId";

export default function PublicSparksPage() {
  const { isAuthenticated, user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const themeKey = user?.theme ?? DEFAULT_THEME;
  const t = getThemeStyles(themeKey);

  const [sparks, setSparks] = useState<Spark[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [votingSparkId, setVotingSparkId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchSparks = async () => {
      try {
        setLoading(true);
        const data = await api.getPublicSparks(60);
        if (!cancelled) {
          setSparks(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : "获取公开作品失败";
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchSparks();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleOpenSpark = (sparkId: number) => {
    navigate(`/sparks/${encodeSparkId(sparkId)}`);
  };

  const handleVote = async (sparkId: number, vote: VoteType) => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    try {
      setVotingSparkId(sparkId);
      const updated = await api.voteSpark(sparkId, vote);
      setSparks((prev) =>
        [...prev]
          .map((spark) => (spark.id === updated.id ? updated : spark))
          .sort((a, b) => {
            if (b.likes === a.likes) {
              return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            }
            return b.likes - a.likes;
          })
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : "投票失败";
      showToast(message, "error");
    } finally {
      setVotingSparkId(null);
    }
  };

  return (
    <div className={`min-h-screen ${t.bg} text-white p-6 font-mono transition-all duration-300`}>
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
          <div className={`text-3xl font-bold tracking-wide drop-shadow-lg ${t.header}`}>🌐 公共作品广场</div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/")}
              className={`px-4 py-2 rounded-xl border-2 font-bold ${t.button2} hover:scale-105 transition`}
            >
              ← 返回首页
            </button>
            <button
              onClick={() => navigate(isAuthenticated ? "/my-sparks" : "/login")}
              className={`px-4 py-2 rounded-xl border-2 font-bold ${t.button3} hover:scale-105 transition`}
            >
              我的作品
            </button>
          </div>
        </div>

        <p className={`text-sm opacity-80 mb-8 ${t.subheader}`}>
          灵感集中营，随时浏览朋友们最新的作品，给喜欢的作品点赞或踩踩。
        </p>

        {loading ? (
          <p className="text-sm opacity-80">正在载入公开作品...</p>
        ) : error ? (
          <p className="text-red-200">{error}</p>
        ) : sparks.length === 0 ? (
          <p className="text-sm opacity-80">还没有公开作品，去首页发布第一个吧！</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {sparks.map((spark) => (
              <article
                key={spark.id}
                className={`rounded-3xl border-4 p-5 shadow-xl flex flex-col gap-3 ${t.card}`}
              >
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold truncate">{spark.name}</h2>
                  <span className="text-xs opacity-70">
                    {new Date(spark.created_at).toLocaleDateString("zh-CN", { month: "short", day: "numeric" })}
                  </span>
                </div>
                {spark.creator_name && (
                  <div className="text-sm opacity-70 flex items-center gap-1">
                    <span>👤</span>
                    <span className="font-medium">{spark.creator_name}</span>
                  </div>
                )}
                <p className="text-sm opacity-80 max-h-16 overflow-hidden text-ellipsis">🪄 {spark.prompt}</p>
                <div className="grid grid-cols-2 gap-3">
                  <LikeButton
                    direction="up"
                    count={spark.likes}
                    loading={votingSparkId === spark.id}
                    disabled={votingSparkId === spark.id}
                    onClick={() => handleVote(spark.id, "like")}
                  />
                  <LikeButton
                    direction="down"
                    count={spark.unlikes}
                    loading={votingSparkId === spark.id}
                    disabled={votingSparkId === spark.id}
                    onClick={() => handleVote(spark.id, "unlike")}
                  />
                </div>
                <button
                  onClick={() => handleOpenSpark(spark.id)}
                  className="mt-3 px-4 py-2 rounded-xl border-2 border-white/40 font-bold hover:scale-105 transition"
                >
                  查看详情
                </button>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


